import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/admin";

// ---------- Tiny presentational primitives ----------

function Tile({ label, value, sublabel, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-[var(--border)] px-6 py-5 bg-[var(--bg)]"
    >
      <div className="font-mono-ui text-[9px] tracking-[0.32em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-serif text-4xl text-[var(--text)] mt-2 leading-none">
        {value}
      </div>
      {sublabel && (
        <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)] mt-3">
          {sublabel}
        </div>
      )}
    </div>
  );
}

function Slide({ chapter, title, kicker, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="border-t border-[var(--border)] py-14"
    >
      <div className="flex items-baseline gap-6 mb-8">
        <span className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[var(--accent)]">
          {chapter}
        </span>
        <h2 className="font-serif text-3xl text-[var(--text)]">{title}</h2>
      </div>
      {kicker && (
        <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed max-w-2xl mb-8">
          {kicker}
        </p>
      )}
      {children}
    </section>
  );
}

// ---------- Charts ----------

const CHART_GRID = "#44403c";
const CHART_TEXT = "#a8a29e";
const CHART_ACCENT = "#d97757";

function VolumeChart({ data }) {
  return (
    <div className="border border-[var(--border)] p-5" data-testid="chart-volume">
      <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
        Chat volume — last 30 days
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="volFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_ACCENT} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_ACCENT} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_GRID} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="date" stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} />
            <YAxis stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} width={32} />
            <Tooltip
              contentStyle={{
                background: "#1c1917",
                border: "1px solid #44403c",
                borderRadius: 0,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
              }}
              labelStyle={{ color: CHART_TEXT }}
            />
            <Area type="monotone" dataKey="count" stroke={CHART_ACCENT} strokeWidth={1.5} fill="url(#volFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DomainsChart({ data }) {
  return (
    <div className="border border-[var(--border)] p-5" data-testid="chart-domains">
      <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
        Top domains queried
      </div>
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ top: 8, right: 20, left: 12, bottom: 0 }}>
            <CartesianGrid stroke={CHART_GRID} strokeDasharray="2 4" horizontal={false} />
            <XAxis type="number" stroke={CHART_TEXT} fontSize={10} tickLine={false} axisLine={{ stroke: CHART_GRID }} />
            <YAxis
              type="category"
              dataKey="domain"
              stroke={CHART_TEXT}
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: CHART_GRID }}
              width={120}
            />
            <Tooltip
              contentStyle={{
                background: "#1c1917",
                border: "1px solid #44403c",
                borderRadius: 0,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
              }}
            />
            <Bar dataKey="count" fill={CHART_ACCENT}>
              {data.map((_, i) => (
                <Cell key={i} fill={i === 0 ? CHART_ACCENT : "#7c4936"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function EngineMixTable({ rows }) {
  return (
    <div className="border border-[var(--border)] p-5" data-testid="engine-mix-table">
      <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
        Engine mix — lifetime
      </div>
      {rows.length === 0 ? (
        <p className="font-sans text-sm text-[var(--text-dim)]">No telemetry yet.</p>
      ) : (
        <table className="w-full font-mono-ui text-[11px]">
          <thead>
            <tr className="text-[var(--text-dim)] tracking-[0.18em] uppercase">
              <th className="text-left py-2 font-normal">Engine</th>
              <th className="text-right py-2 font-normal">Calls</th>
              <th className="text-right py-2 font-normal">Success</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.engine} className="border-t border-[var(--border-soft)]">
                <td className="py-2 text-[var(--text)]">{r.engine}</td>
                <td className="py-2 text-right text-[var(--text-muted)]">{r.calls.toLocaleString()}</td>
                <td className="py-2 text-right text-[var(--accent)]">
                  {(r.success_rate * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------- Page ----------

export default function InvestorDashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [overview, setOverview] = useState(null);
  const [health, setHealth] = useState(null);
  const [subs, setSubs] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user.role === "admin") {
      Promise.all([
        adminApi.dashboardMetrics(),
        adminApi.dashboardOverview(),
        adminApi.dashboardHealth(),
        adminApi.dashboardSubscriptions(),
      ])
        .then(([m, o, h, s]) => {
          setMetrics(m); setOverview(o); setHealth(h); setSubs(s);
        })
        .catch((e) => setErr(e.message || "Failed to load dashboard"));
    }
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/investors" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const loading = !metrics && !err;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/admin"
          data-testid="investor-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Admin
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          Investor Brief
        </h1>
        <Link
          to="/metrics"
          data-testid="link-metrics"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--accent)] hover:underline"
        >
          PR Metrics →
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        {/* Hero */}
        <section className="py-16 border-b border-[var(--border)]">
          <div className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[var(--accent)] mb-6">
            Humane Behavior AI · Internal Investor Brief
          </div>
          <h1
            data-testid="investor-hero"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--text)] max-w-3xl"
          >
            Counterfactual reasoning over a 45K+ real-world case corpus —
            not a chatbot pretending to be a therapist.
          </h1>
          <p className="font-sans text-base text-[var(--text-muted)] mt-8 max-w-2xl leading-relaxed">
            Live numbers below pull from production telemetry the moment this
            page opens. No mocked figures, no curated dates.
          </p>
        </section>

        {err && (
          <div data-testid="investor-error" className="mt-10 border border-[var(--accent)] px-5 py-4 font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[var(--accent)]">
            {err}
          </div>
        )}

        {loading && (
          <div className="py-24 flex items-center justify-center gap-3 text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
            <Loader2 className="animate-spin" size={14} /> Loading live numbers…
          </div>
        )}

        {metrics && overview && (
          <>
            {/* Activity */}
            <Slide
              chapter="01 · Engagement"
              title="Daily, weekly, monthly active users"
              kicker="DAU/WAU/MAU computed from distinct user_ids that hit the chat endpoint in the last 1 / 7 / 30 days."
              testid="slide-activity"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile label="DAU" value={metrics.activity.dau} testid="tile-dau" />
                <Tile label="WAU" value={metrics.activity.wau} testid="tile-wau" />
                <Tile label="MAU" value={metrics.activity.mau} testid="tile-mau" />
                <Tile
                  label="Stickiness"
                  value={`${(metrics.activity.stickiness_dau_mau * 100).toFixed(1)}%`}
                  sublabel="DAU ÷ MAU"
                  testid="tile-stickiness"
                />
              </div>
            </Slide>

            {/* Volume */}
            <Slide
              chapter="02 · Volume"
              title="Conversation throughput"
              kicker="One bar per day. Includes anonymous trials and authenticated sessions."
              testid="slide-volume"
            >
              <VolumeChart data={metrics.volume_by_day_30d} />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                <Tile label="Total chats" value={overview.chats.total.toLocaleString()} testid="tile-chats-total" />
                <Tile label="Chats · 24h" value={overview.chats.last_24h.toLocaleString()} testid="tile-chats-24h" />
                <Tile label="Users · total" value={overview.users.total.toLocaleString()} testid="tile-users-total" />
              </div>
            </Slide>

            {/* Topics + RAG */}
            <Slide
              chapter="03 · Topics + RAG"
              title="What people actually ask"
              kicker="Classification is performed by the gating model on every turn; RAG hit-rate measures how often the case corpus surfaced at least one relevant precedent."
              testid="slide-topics"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <DomainsChart data={metrics.top_domains} />
                <div className="border border-[var(--border)] p-5" data-testid="rag-panel">
                  <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                    RAG performance
                  </div>
                  <div className="space-y-4">
                    <Tile label="Retrievals" value={metrics.rag.total_retrievals.toLocaleString()} testid="tile-rag-retrievals" />
                    <Tile label="With ≥1 hit" value={metrics.rag.with_hits.toLocaleString()} testid="tile-rag-hits" />
                    <Tile
                      label="Hit rate"
                      value={`${(metrics.rag.hit_rate * 100).toFixed(1)}%`}
                      sublabel="cases surfaced ÷ retrievals"
                      testid="tile-rag-hit-rate"
                    />
                  </div>
                </div>
              </div>
            </Slide>

            {/* Engines + Corpus */}
            <Slide
              chapter="04 · Infrastructure"
              title="Models in production"
              kicker="Gemini 2.5 Flash is primary; Claude Sonnet 4.6 takes over on Gemini 4xx/5xx. Both run with the same Counterfactual Adversarial Reasoning prompt."
              testid="slide-engines"
            >
              <div className="grid md:grid-cols-2 gap-4 mb-5">
                <EngineMixTable rows={metrics.engine_mix} />
                <div className="border border-[var(--border)] p-5">
                  <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                    Corpus
                  </div>
                  <div className="space-y-3">
                    <Tile label="Cases indexed" value={metrics.corpus.cases.toLocaleString()} testid="tile-corpus-cases" />
                    <Tile label="Domains" value={metrics.corpus.domains} testid="tile-corpus-domains" />
                    <Tile label="Datasets merged" value={metrics.corpus.datasets} testid="tile-corpus-datasets" />
                  </div>
                </div>
              </div>
              {health && (
                <div className="border border-[var(--border)] p-5" data-testid="health-24h">
                  <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
                    24-hour engine health
                  </div>
                  {health.engines_24h.length === 0 ? (
                    <p className="font-sans text-sm text-[var(--text-dim)]">No engine telemetry in the last 24h.</p>
                  ) : (
                    <table className="w-full font-mono-ui text-[11px]">
                      <thead>
                        <tr className="text-[var(--text-dim)] tracking-[0.18em] uppercase">
                          <th className="text-left py-2 font-normal">Engine</th>
                          <th className="text-right py-2 font-normal">Calls</th>
                          <th className="text-right py-2 font-normal">Success</th>
                          <th className="text-right py-2 font-normal">Avg latency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {health.engines_24h.map((e) => (
                          <tr key={e.engine} className="border-t border-[var(--border-soft)]">
                            <td className="py-2 text-[var(--text)]">{e.engine}</td>
                            <td className="py-2 text-right text-[var(--text-muted)]">{e.calls_24h}</td>
                            <td className="py-2 text-right text-[var(--accent)]">{(e.success_rate * 100).toFixed(1)}%</td>
                            <td className="py-2 text-right text-[var(--text-muted)]">{e.avg_latency_ms} ms</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </Slide>

            {/* Evaluation */}
            <Slide
              chapter="05 · Evaluation"
              title="Side-by-side runs we keep"
              kicker="Every adversarial eval is stored: query → 3 branches (Gemini HBM, Claude HBM, idealized baseline) → human vote. The vote count is the truth metric, not the run count."
              testid="slide-eval"
            >
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Tile label="Eval runs" value={metrics.eval.runs.toLocaleString()} testid="tile-eval-runs" />
                <Tile label="Human votes" value={metrics.eval.votes.toLocaleString()} testid="tile-eval-votes" />
                <Tile label="Privacy violations" value={overview.privacy.violations.toLocaleString()} sublabel="anon save attempts" testid="tile-privacy" />
              </div>
            </Slide>

            {/* Subscriptions */}
            {subs && (
              <Slide
                chapter="06 · Monetisation"
                title="Tier distribution"
                kicker="Stripe is not wired yet. Tier field exists; once the checkout lands, this slide flips to MRR / churn / plan-change events."
                testid="slide-subs"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(subs.tier_counts).map(([tier, count]) => (
                    <Tile
                      key={tier}
                      label={tier}
                      value={count}
                      sublabel="users"
                      testid={`tile-tier-${tier}`}
                    />
                  ))}
                </div>
                <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mt-5">
                  Stripe: {subs.stripe_ready ? "ready" : "pending"} · MRR: ${subs.mrr}
                </p>
              </Slide>
            )}
          </>
        )}
      </main>
    </div>
  );
}
